// dependencies
import React, { useEffect, useState } from "react";

// components
import { DateRange, DateRangePicker } from "@performyard/py-components";

import OptionsDrawer from "../../../../components/OptionsDrawer";
import Tooltip from "../../../../components/Tooltip";

// config
import Config from "../../Config";
// styles
import css from "./DatePickerMenu.module.scss";

export interface SelectionOption {
	label: string;
	value: string;
	tooltip?: string;
}

interface BaseProps {
	handleSelect: (type: "default" | "custom", value: SelectionOption | DateRange) => void;
	options: SelectionOption[];
	selected: SelectionOption["value"];
}

const drawerOptions = [
	{
		label: "Custom Date Range",
		value: "custom",
	},
	{
		label: "Default Date Filters",
		value: "default",
	},
];

const DatePickerMenu = (props: BaseProps) => {
	const { options, selected, handleSelect } = props;

	const [selectedDrawerOption, setSelectedDrawerOption] = useState<"default" | "custom">("default");

	const [dateRange, setDateRange] = useState<DateRange | undefined>();

	useEffect(() => {
		if (typeof selected === "string") {
			setSelectedDrawerOption("default");
		} else {
			setSelectedDrawerOption("custom");
			setDateRange(selected);
		}
	}, [selected]);

	const getChipClass = (index: number) => {
		let chipClass = css.chip;
		if (index === 0) {
			chipClass = `${chipClass} ${css.firstChip}`;
		}
		return chipClass;
	};

	const handleSelectOption = (option) => {
		setSelectedDrawerOption(option.value);
	};

	return (
		<div role="radiogroup" className={css.allChips}>
			{selectedDrawerOption === "default" && (
				<>
					{options.map((option, index) => {
						return (
							<Tooltip
								key={option.value}
								message={option?.tooltip ? option.tooltip : ""}
								placement="bottom"
								disabled={option?.tooltip ? false : true}
								revealed
							>
								<div
									role="radio"
									tabIndex={index}
									aria-checked={selected === option.value}
									data-testid={`date-range-${option.value}`}
									className={`${getChipClass(index)} ${selected === option.value ? css.selected : ""}`}
									onClick={() => handleSelect(selectedDrawerOption, option)}
								>
									<div className="text-paragraph-3 font-regular">{option.label}</div>
								</div>
							</Tooltip>
						);
					})}
				</>
			)}
			{selectedDrawerOption === "custom" && (
				<div>
					<DateRangePicker
						id="date-range-picker"
						value={dateRange}
						onChange={(val) => setDateRange(val)}
						onOpenChange={(isOpen) => {
							// if custom date range is valid, handle date range selection
							if (!isOpen && dateRange?.from && dateRange?.to) {
								handleSelect(selectedDrawerOption, dateRange);
							}
						}}
						startDate={Config.ENGAGEMENT_RELEASE_DATE}
						endDate={new Date()}
						// styles to fit the daterange picker inside the date picker menu
						popoverTriggerClassName="border-1 w-[259.41px] border-solid border-neutral-5 rounded-r-none border-0 border-r border-neutral-3"
						popoverContentClassName="left-8"
					/>
				</div>
			)}
			<div className={` ${css.lastChip} bg-neutral-0 p-1`} data-testid="date-range-custom">
				<OptionsDrawer
					additionalClasses={css.optionsButtonAdditionalClasses}
					optionClasses={css.optionButtonClasses}
					openOn="bottom-end"
					horizontal={true}
					options={drawerOptions.filter((option) => option.value !== selectedDrawerOption)}
					onSelect={handleSelectOption}
					onOpen={() => {}}
					optionWidth={114}
				/>
			</div>
		</div>
	);
};

export default DatePickerMenu;
